import React, { useState } from 'react';

import {
  PlusSquareOutlined,
  MinusSquareOutlined,
  EditOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { Button, ModalClose, Spinner } from '../../../../components';
import { useMutation } from '@apollo/client';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';

import {
  UPDATE_DIVISION_LEVEL_PERCENTAGE,
  UPDATE_PRODUCT_GROUP_LEVEL_PERCENTAGE,
  UPDATE_PRODUCT_LEVEL_PERCENTAGE,
} from '../../queries';
import { Products } from '../DependencyMatrix';
import Sucess from '../../../../components/generic/sucess';
import Loading from '../../../../components/generic/Loading';
import TextArea from 'antd/es/input/TextArea';
import { EDIT_LOCATION_LEVEL_COMMENT } from '../../queries/Levels';
import { LOCATION_TYPES } from '../../../../constants';

interface LevelMappingFormProps {
  onCancel: () => void;
  data?: Products;
  product?: string;
  parentLocation?: string;
  level?: number;
  locationLevel?: string;
  isEditing: boolean;
}

export function LevelMappingForm({
  onCancel,
  data,
  product,
  parentLocation,
  level,
  locationLevel,
  isEditing,
}: LevelMappingFormProps) {
  const mutation =
    product === 'DivisionMatrix'
      ? UPDATE_DIVISION_LEVEL_PERCENTAGE
      : product === 'ProductGroupMatrix'
      ? UPDATE_PRODUCT_GROUP_LEVEL_PERCENTAGE
      : UPDATE_PRODUCT_LEVEL_PERCENTAGE;
  const [valid, setValid] = useState<string>('');
  const [sucess, setSucess] = useState(false);
  const [updateLevelPercentage, { loading, error }] = useMutation(mutation, {
    refetchQueries: ['DivisionSelectionDashboard', 'GetMapping'],
  });
  const [updateDependencyComment, { loading: loadingUpdateComment }] =
    useMutation(EDIT_LOCATION_LEVEL_COMMENT, {
      refetchQueries: ['DivisionSelectionDashboard', 'GetMapping'],
    });
  const [editingFields, seteditingFields] = useState<any>(
    data?.locationAuxs?.map((item: any) => false),
  );
  const productId = data?.key!;

  const { register, control, handleSubmit, getValues } = useForm({
    defaultValues: data,
  });

  const { fields } = useFieldArray({
    control,
    name: 'locationAuxs',
  });

  if (loading) return <Spinner />;
  if (error) return <>Error! {error.message}</>;

  const enableEdit = (index: number) => {
    seteditingFields(
      editingFields?.map((item: any, i: number) =>
        i === index ? !item : item,
      ),
    );
  };

  const createComment = async (index: number) => {
    let response;
    const values = getValues();
    const { key, comment } = values.locationAuxs[index];

    try {
      response = await updateDependencyComment({
        variables: {
          input: {
            division: { uuid: productId },
            location: { uuid: locationLevel },
            locationAux: { uuid: key },
            parentLocation: { uuid: parentLocation! },
            level: level!,
            comment,
            percentage: 0,
          },
        },
      });
      if (response.data) {
        console.log('Mutation successful');
      }
    } catch (err) {
      console.log(err);
    }

    setSucess(true);
    setTimeout(() => {
      setSucess(false);
      enableEdit(index);
    }, 2000);
  };

  const onFinish: SubmitHandler<Products> = async (values) => {
    let response;

    setValid('');
    values?.locationAuxs.map(async (item) => {
      const percentage = parseFloat(item.percentage.toString());
      const watchValue = data?.locationAuxs?.find(
        (location) =>
          location.key === item.key && location.percentage !== percentage,
      );

      if (product == 'DivisionMatrix' && watchValue) {
        try {
          response = await updateLevelPercentage({
            variables: {
              data: {
                division: { uuid: productId },
                location: { uuid: locationLevel },
                percentage: percentage,
                locationAux: { uuid: item.key },
                parentLocation: { uuid: parentLocation! },
                level: level!,
              },
            },
          });
          if (response.data) {
            console.log('Mutation successful');
            onCancel();
          }
        } catch (err) {
          console.log(err);
        }
      } else if (product == 'ProductGroupMatrix' && watchValue) {
        try {
          response = await updateLevelPercentage({
            variables: {
              data: {
                productGroup: { uuid: productId },
                location: { uuid: locationLevel },
                percentage: percentage,
                locationAux: { uuid: item.key },
                parentLocation: { uuid: parentLocation! },
                level: level!,
              },
            },
          });
          if (response.data) {
            console.log('Mutation successful');
            onCancel();
          }
        } catch (err) {
          console.log(err);
        }
      } else if (product == 'ProductMatrix' && watchValue) {
        try {
          response = await updateLevelPercentage({
            variables: {
              data: {
                product: { uuid: productId },
                location: { uuid: locationLevel },
                percentage: percentage,
                locationAux: { uuid: item.key },
                parentLocation: { uuid: parentLocation! },
                level: level!,
              },
            },
          });
          if (response.data) {
            console.log('Mutation successful');
            onCancel();
          }
        } catch (err) {
          console.log(err);
        }
      }
    });
  };

  return (
    <form
      className="  w-full flex flex-col flex-nowrap mt-2"
      onSubmit={handleSubmit(onFinish)}
    >
      <h1 className="  font-bold text-lg tracking-widest">{data?.name}</h1>
      {data?.locationAuxs?.map((location, index) => (
        <div className="inline-flex flex-row flex-nowrap items-center my-2 justify-between">
          <section
            key={`section-${location.key}`}
            className={`flex w-full justify-between items-center `}
          >
            <label
              className="font-semibold text-primary-200 text-sm flex flex-col gap-1"
              htmlFor={`${location.key}`}
            >
              {location.name}
              {location.locationType === LOCATION_TYPES.DISTRIBUTION_CENTER && (
                <span className="label-tag rounded-md">
                  Distribution Center
                </span>
              )}
            </label>
            {isEditing && (
              <div className="mx-4 border rounded-lg text-primary-200 font-normal text-sm px-2 py-1 flex items-center">
                <input
                  placeholder="%"
                  className="w-24 bg-transparent border-none text-right text-primary-200 font-normal text-sm focus:outline-none focus:ring-0"
                  type="number"
                  step={0.01}
                  id={location.key}
                  defaultValue={location.percentage}
                  {...register(`locationAuxs.${index}.percentage`, {
                    required: true,
                    min: {
                      value: 0,
                      message: 'Percentage must be greater than 0.',
                    },
                    max: {
                      value: 100,
                      message: 'Percentage must be less than 100.',
                    },
                  })}
                />
                <span className="w-full">%</span>
              </div>
            )}
          </section>
          {editingFields[index] && loadingUpdateComment ? (
            <Loading />
          ) : sucess && editingFields[index] ? (
            <Sucess />
          ) : (
            <section className={`relative ${isEditing ? 'w-6/12' : 'w-3/4'} `}>
              <Controller
                control={control}
                defaultValue={location?.comment}
                name={`locationAuxs.${index}.comment`}
                render={({ field }) => (
                  <TextArea
                    autoSize={{
                      minRows: !editingFields[index] ? 2 : 4,
                      maxRows: !editingFields[index] ? 2 : 6,
                    }}
                    readOnly={isEditing || !editingFields[index]}
                    className={`rounded-lg pr-8 w-full `}
                    id={`comment-${location.key}`}
                    value={field.value || ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />
              <div
                className={`bg-white absolute ${
                  location?.comment === '' ? '' : 'top-4 -translate-y-1/2'
                } transform top-2 right-2`}
              >
                {!editingFields[index] ? (
                  !isEditing ? (
                    <EditOutlined
                      className="text-blue hover:cursor-pointer"
                      onClick={() => enableEdit(index)}
                    />
                  ) : (
                    <PlusSquareOutlined
                      className="text-blue hover:cursor-pointer"
                      onClick={() => enableEdit(index)}
                    />
                  )
                ) : !isEditing ? (
                  <div
                    className={`flex items-center gap-2 ${
                      location?.comment !== ''
                        ? 'absolute -top-1.5 right-0'
                        : ''
                    }`}
                  >
                    <SaveOutlined
                      className="text-blue hover:cursor-pointer"
                      onClick={() => createComment(index)}
                    />
                    <ModalClose
                      className="w-3 h-3 fill-blue hover:cursor-pointer"
                      onClick={() => enableEdit(index)}
                    />
                  </div>
                ) : (
                  <MinusSquareOutlined
                    className="text-blue hover:cursor-pointer"
                    onClick={() => enableEdit(index)}
                  />
                )}
              </div>
            </section>
          )}
        </div>
      ))}
      {isEditing && <div className="text-red-500 text-sm">{valid}</div>}
      {isEditing && (
        <div className="inline-flex flex-row justify-center mt-6">
          <Button htmlType={'submit'} className="primary-button">
            Save
          </Button>
        </div>
      )}
    </form>
  );
}
